/* ============================================
   Artemis Theme — Bulk Personalization
   --------------------------------------------
   On a personalizable PDP the customer can order many engraved items at once,
   each with its own personalization. Two ways in:
     • Upload a spreadsheet — download a CSV template (opens in Excel, Numbers,
       Google Sheets), fill one row per recipient, save as CSV, upload.
     • Enter manually — an on-page grid: one row per recipient, no file needed.
   Either way, each row becomes its own line item (variant + line-item
   properties), so the merchant receives a distinct, fully-personalized item.

   TEMPLATES WITH VARIABLES
   The customer can turn the personalization lines into a fill-in template by
   typing {tokens}, e.g.
       Line 1: 2026 Champion
       Line 2: {first_name} {last_name}
       Line 3: {event_name}
   When any {token} is present we switch to "template mode": the columns become
   the unique tokens (not the whole lines), so the customer only fills in the
   variables. On add, each row's tokens are substituted back into the line
   templates — the fixed text ("2026 Champion") stays constant on every item.

   With no {tokens}, columns are simply the line labels and each cell is the
   full line text (the original behavior).

   Order-level fields (e.g. the single Requested Ship Date, asked once) are
   date/checkbox inputs, copied onto every row as common properties.
   ============================================ */

(function () {
  'use strict';

  var container = document.querySelector('[data-bulk-personalize]');
  if (!container) return;

  var form = document.getElementById('product-form');
  var variantInput = document.getElementById('product-variant-id');
  if (!form || !variantInput) return;

  var tabs = container.querySelectorAll('[data-bulk-method]');
  var panels = container.querySelectorAll('[data-bulk-panel]');
  var manualPanel = container.querySelector('[data-bulk-panel="manual"]');
  var templateBtn = container.querySelector('[data-bulk-template]');
  var fileInput = container.querySelector('[data-bulk-file]');
  var fileNameEl = container.querySelector('[data-bulk-filename]');
  var previewEl = container.querySelector('[data-bulk-preview]');
  var gridEl = container.querySelector('[data-bulk-grid]');
  var addRowBtn = container.querySelector('[data-bulk-addrow]');
  var varsEl = container.querySelector('[data-bulk-vars]');
  var statusEl = container.querySelector('[data-bulk-status]');
  var addBtn = container.querySelector('[data-bulk-add]');
  var proofBtn = container.querySelector('[data-bulk-proof]');
  var singleAddBtn = document.getElementById('add-to-cart');

  var ADD_BATCH = 50;     // items per /cart/add.js request
  var MAX_ROWS = 500;     // sanity cap; larger lists should be split into orders
  var PREVIEW_ROWS = 5;
  var INITIAL_ROWS = 3;
  var TOKEN_RE = /\{([^{}]+)\}/g;

  var activeMethod = 'manual';
  var uploadRows = [];    // raw rows keyed by current columns, parsed from a file
  var gridSig = '';       // column signature the manual grid was built for

  /* --- Fields, columns & common props (read live off the form) --- */
  function propKeyFromName(name) {
    var m = /^properties\[(.+)\]$/.exec(name || '');
    return m ? m[1] : null;
  }

  // The personalization lines = the text inputs. In template mode their values
  // are templates; otherwise they're the per-row text.
  function getLineFields() {
    var fields = [];
    form.querySelectorAll('input[type="text"][name^="properties["]').forEach(function (el) {
      var key = propKeyFromName(el.getAttribute('name'));
      if (key) fields.push({ label: key, el: el });
    });
    return fields;
  }
  function getLineLabels() {
    return getLineFields().map(function (f) { return f.label; });
  }

  // Unique {tokens} across all lines, in order of first appearance.
  function getTemplateTokens() {
    var seen = {}, tokens = [];
    getLineFields().forEach(function (f) {
      var m;
      TOKEN_RE.lastIndex = 0;
      while ((m = TOKEN_RE.exec(f.el.value)) !== null) {
        var name = m[1].trim();
        if (name && !seen[name]) { seen[name] = 1; tokens.push(name); }
      }
    });
    return tokens;
  }
  function isTemplateMode() { return getTemplateTokens().length > 0; }

  // Columns the customer fills in: the tokens (template mode) or the line labels.
  function getColumns() {
    var tokens = getTemplateTokens();
    return tokens.length ? tokens : getLineLabels();
  }

  // Turn one raw row into the final line-item personalization properties
  // (keyed by line label), substituting tokens where present.
  function resolveRow(row) {
    var props = {};
    if (isTemplateMode()) {
      getLineFields().forEach(function (f) {
        var resolved = f.el.value.replace(TOKEN_RE, function (_, name) {
          var v = row[name.trim()];
          return v != null ? v : '';
        }).trim();
        if (resolved) props[f.label] = resolved;
      });
    } else {
      getLineFields().forEach(function (f) {
        var v = (row[f.label] || '').trim();
        if (v) props[f.label] = v;
      });
    }
    return props;
  }

  // Order-level props (date, checkbox…), applied identically to every row.
  // The Requested Ship Date lives here, so it's only ever asked once.
  function getCommonProps() {
    var props = {};
    form.querySelectorAll('[name^="properties["]').forEach(function (el) {
      if (el.type === 'text') return;            // text = per-row line
      var key = propKeyFromName(el.getAttribute('name'));
      if (!key) return;
      if (el.type === 'checkbox') {
        if (el.checked) props[key] = el.value || 'Yes';
        return;
      }
      var val = (el.value || '').trim();
      if (val) props[key] = val;
    });
    return props;
  }

  /* --- Small utilities --- */
  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
  function csvCell(s) {
    s = String(s == null ? '' : s);
    return /[",\r\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  }
  function setStatus(msg, isError) {
    statusEl.textContent = msg || '';
    statusEl.classList.toggle('product-page__bulk-status--error', !!isError);
  }
  function pluralItems(n) { return n + ' personalized item' + (n === 1 ? '' : 's'); }

  /* --- Live template state: variables hint + single-add guard + grid sync --- */
  function refreshTemplateState() {
    var tokens = getTemplateTokens();
    if (varsEl) {
      if (tokens.length) {
        varsEl.innerHTML = 'Template variables detected: ' +
          tokens.map(function (t) { return '<code>' + escapeHtml(t) + '</code>'; }).join(', ') +
          '. The columns below ask for just these — your fixed text stays the same on every item.';
        varsEl.classList.add('product-page__bulk-vars--active');
      } else {
        varsEl.innerHTML = 'Variables are optional. By default each personalization line is its own ' +
          'column and you fill in the full text for every item. If most of the text stays the same, you ' +
          'can <em>optionally</em> add a variable in the lines above with curly braces, e.g. ' +
          '<code>{first_name}</code> — then only that variable becomes a column to fill in, so you enter ' +
          'just what changes from one item to the next.';
        varsEl.classList.remove('product-page__bulk-vars--active');
      }
    }
    // Rebuild the manual grid if it's showing and the columns changed.
    if (manualPanel && !manualPanel.hidden && getColumns().join('|') !== gridSig) buildGrid();
  }

  /* --- Method tabs --- */
  function setMethod(method) {
    activeMethod = method;
    tabs.forEach(function (t) {
      var on = t.getAttribute('data-bulk-method') === method;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    panels.forEach(function (p) { p.hidden = p.getAttribute('data-bulk-panel') !== method; });
    setStatus('');
    if (method === 'manual' && gridEl && (!gridEl.querySelector('table') || getColumns().join('|') !== gridSig)) buildGrid();
  }

  /* --- Manual entry grid --- */
  function buildGrid() {
    var cols = getColumns();
    gridSig = cols.join('|');
    if (!cols.length) { gridEl.innerHTML = '<p class="product-page__bulk-more">No personalization fields configured.</p>'; return; }
    var html = '<table class="product-page__bulk-table"><thead><tr>';
    cols.forEach(function (c) { html += '<th>' + escapeHtml(c) + '</th>'; });
    html += '<th class="product-page__bulk-rmcol" aria-label="Remove"></th></tr></thead><tbody></tbody></table>';
    gridEl.innerHTML = html;
    for (var i = 0; i < INITIAL_ROWS; i++) addRow();
  }

  function addRow(values) {
    var tbody = gridEl.querySelector('tbody');
    if (!tbody) return;
    var cols = getColumns();
    var tr = document.createElement('tr');
    cols.forEach(function (c) {
      var td = document.createElement('td');
      var input = document.createElement('input');
      input.type = 'text';
      input.className = 'input product-page__bulk-cell';
      input.setAttribute('data-bulk-cell', '');
      input.setAttribute('data-col', c);
      input.setAttribute('aria-label', c);
      if (values && values[c]) input.value = values[c];
      td.appendChild(input);
      tr.appendChild(td);
    });
    var tdRm = document.createElement('td');
    var rm = document.createElement('button');
    rm.type = 'button';
    rm.className = 'product-page__bulk-rowremove';
    rm.setAttribute('aria-label', 'Remove row');
    rm.textContent = '×';
    rm.addEventListener('click', function () {
      tr.remove();
      if (!tbody.querySelector('tr')) addRow();   // never leave the grid empty
    });
    tdRm.appendChild(rm);
    tr.appendChild(tdRm);
    tbody.appendChild(tr);
  }

  function collectManualRows() {
    var rows = [];
    gridEl.querySelectorAll('tbody tr').forEach(function (tr) {
      var obj = {}, has = false;
      tr.querySelectorAll('[data-bulk-cell]').forEach(function (inp) {
        var v = (inp.value || '').trim();
        if (v) { obj[inp.getAttribute('data-col')] = v; has = true; }
      });
      if (has) rows.push(obj);
    });
    return rows;
  }

  /* --- CSV: template download + upload parse --- */
  function downloadTemplate() {
    var cols = getColumns();
    if (!cols.length) { setStatus('No personalization fields found to build a template.', true); return; }
    var csv = cols.map(csvCell).join(',') + '\r\n';
    var handle = (window.productData && window.productData.handle) || 'personalization';
    var blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    var url = URL.createObjectURL(blob);
    var a = document.createElement('a');
    a.href = url;
    a.download = handle + '-personalization-template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // RFC-4180-ish: quoted fields, "" escapes, CRLF/LF, leading BOM.
  function parseCSV(text) {
    if (text.charCodeAt(0) === 0xFEFF) text = text.slice(1);
    var rows = [], row = [], field = '', inQuotes = false, i = 0;
    while (i < text.length) {
      var c = text[i];
      if (inQuotes) {
        if (c === '"') {
          if (text[i + 1] === '"') { field += '"'; i += 2; continue; }
          inQuotes = false; i++; continue;
        }
        field += c; i++; continue;
      }
      if (c === '"') { inQuotes = true; i++; continue; }
      if (c === ',') { row.push(field); field = ''; i++; continue; }
      if (c === '\r') { i++; continue; }
      if (c === '\n') { row.push(field); rows.push(row); row = []; field = ''; i++; continue; }
      field += c; i++;
    }
    if (field !== '' || row.length) { row.push(field); rows.push(row); }
    return rows;
  }

  // Preview shows the RESOLVED engraving lines (what will actually be ordered),
  // so the customer sees the fixed text + their values combined.
  function renderPreview(rawRows) {
    var labels = getLineLabels();
    var resolved = rawRows.slice(0, PREVIEW_ROWS).map(resolveRow);
    var html = '<table class="product-page__bulk-table"><thead><tr>';
    labels.forEach(function (c) { html += '<th>' + escapeHtml(c) + '</th>'; });
    html += '</tr></thead><tbody>';
    resolved.forEach(function (r) {
      html += '<tr>';
      labels.forEach(function (c) { html += '<td>' + escapeHtml(r[c] || '') + '</td>'; });
      html += '</tr>';
    });
    html += '</tbody></table>';
    if (rawRows.length > resolved.length) {
      html += '<p class="product-page__bulk-more">+ ' + (rawRows.length - resolved.length) + ' more…</p>';
    }
    previewEl.innerHTML = html;
    previewEl.hidden = false;
  }

  function handleFile(file) {
    uploadRows = [];
    previewEl.hidden = true;
    if (!file) return;
    if (fileNameEl) fileNameEl.textContent = file.name;

    var reader = new FileReader();
    reader.onload = function () {
      var cols = getColumns();
      if (!cols.length) { setStatus('No personalization fields are configured on this product.', true); return; }

      var grid = parseCSV(String(reader.result || ''));
      if (grid.length < 2) { setStatus('That file has no data rows. Download the template and add one row per item.', true); return; }

      var norm = function (s) { return (s || '').toLowerCase().replace(/\s+/g, ' ').trim(); };
      var colByIndex = grid[0].map(function (h) {
        for (var k = 0; k < cols.length; k++) { if (norm(cols[k]) === norm(h)) return cols[k]; }
        return null;
      });
      if (!colByIndex.some(Boolean)) {
        setStatus('None of the CSV columns matched the expected fields. Download a fresh template — its columns reflect the variables you’ve set.', true);
        return;
      }

      var rows = [];
      for (var r = 1; r < grid.length; r++) {
        var cells = grid[r], obj = {}, hasValue = false;
        for (var ci = 0; ci < cells.length; ci++) {
          var col = colByIndex[ci];
          if (!col) continue;
          var v = (cells[ci] || '').trim();
          if (v) { obj[col] = v; hasValue = true; }
        }
        if (hasValue) rows.push(obj);
      }
      if (!rows.length) { setStatus('No filled-in rows found in that file.', true); return; }

      var capped = rows.length > MAX_ROWS;
      if (capped) rows = rows.slice(0, MAX_ROWS);

      uploadRows = rows;
      renderPreview(rows);
      setStatus(
        capped
          ? 'Loaded the first ' + MAX_ROWS + ' rows (file exceeds the ' + MAX_ROWS + '-item limit — please split larger orders).'
          : pluralItems(rows.length) + ' ready to add.',
        capped
      );
    };
    reader.onerror = function () { setStatus('Could not read that file. Please try again.', true); };
    reader.readAsText(file);
  }

  /* --- Add to cart (chunked) --- */
  function postChunk(items) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (res) {
      if (!res.ok) return res.json().then(function (e) { throw new Error(e && e.description ? e.description : 'Add to cart failed'); });
      return res.json();
    });
  }

  function rowsForAdd() {
    var rows = activeMethod === 'manual' ? collectManualRows() : uploadRows;
    return rows.length > MAX_ROWS ? rows.slice(0, MAX_ROWS) : rows;
  }

  function addAll() {
    var rows = rowsForAdd();
    if (!rows.length) {
      setStatus(activeMethod === 'manual' ? 'Enter at least one row first.' : 'Upload a filled-in CSV first.', true);
      return;
    }

    // Enforce the form's own constraints first (e.g. required ship date + its
    // minimum lead time) — the same validation a normal add-to-cart runs.
    if (typeof form.reportValidity === 'function' && !form.reportValidity()) return;

    var variantId = variantInput.value;
    if (!variantId) { setStatus('Please choose the product options first.', true); return; }

    var common = getCommonProps();
    var items = [];
    rows.forEach(function (row) {
      var props = resolveRow(row);
      if (!Object.keys(props).length) return;     // nothing to personalize on this row
      Object.keys(common).forEach(function (k) { props[k] = common[k]; });
      items.push({ id: variantId, quantity: 1, properties: props });
    });
    if (!items.length) { setStatus('No personalization values found in your rows.', true); return; }

    var batches = [];
    for (var i = 0; i < items.length; i += ADD_BATCH) batches.push(items.slice(i, i + ADD_BATCH));

    addBtn.disabled = true;
    var original = addBtn.textContent;
    addBtn.textContent = 'Adding…';
    setStatus('Adding ' + pluralItems(items.length) + '…');

    var added = 0;
    batches.reduce(function (chain, batch) {
      return chain.then(function () { return postChunk(batch).then(function () { added += batch.length; }); });
    }, Promise.resolve())
      .then(function () {
        setStatus('Added ' + pluralItems(added) + ' — taking you to your cart…');
        window.location.href = '/cart';
      })
      .catch(function (err) {
        addBtn.disabled = false;
        addBtn.textContent = original;
        setStatus(
          (added ? added + ' item(s) were added before an error occurred. ' : '') +
          (err && err.message ? err.message : 'Something went wrong — please try again.'),
          true
        );
      });
  }

  /* --- Download proof of every row --- */
  // Each row → one resolved engraving block (its line values in field order);
  // the shared proof engine tiles them across a sheet sized to the selected
  // variant's plate. Available only when proof.js loaded + a proof size exists.
  function proofAvailable() {
    if (!proofBtn || !window.ArtemisProof || !window.PROOF_DIMENSIONS) return false;
    // Any variant with a configured size is enough to offer the button; a
    // variant-specific gap is reported by the proof engine at click time.
    return Object.keys(window.PROOF_DIMENSIONS).some(function (id) { return window.PROOF_DIMENSIONS[id]; });
  }

  function downloadProof() {
    var rows = rowsForAdd();
    if (!rows.length) {
      setStatus(activeMethod === 'manual' ? 'Enter at least one row first.' : 'Upload a filled-in CSV first.', true);
      return;
    }
    var labels = getLineLabels();
    var blocks = rows.map(function (row) {
      var props = resolveRow(row);
      return labels.map(function (l) { return props[l]; });   // engine cleans/trims/drops blanks
    });
    var dims = (window.PROOF_DIMENSIONS || {})[variantInput.value];
    window.ArtemisProof.generate({
      blocks: blocks,
      dims: dims,
      imageUrl: dims && dims.image,
      title: (window.productData && window.productData.title) || '',
      filename: ((window.productData && window.productData.handle) || 'engraving') + '-proofs.pdf',
      onStatus: setStatus
    });
  }

  /* --- Wire up --- */
  // Track the personalization lines so the variables hint / columns stay live.
  getLineFields().forEach(function (f) { f.el.addEventListener('input', refreshTemplateState); });

  tabs.forEach(function (t) {
    t.addEventListener('click', function () { setMethod(t.getAttribute('data-bulk-method')); });
  });
  if (templateBtn) templateBtn.addEventListener('click', downloadTemplate);
  if (fileInput) fileInput.addEventListener('change', function () { handleFile(fileInput.files[0]); });
  if (addRowBtn) addRowBtn.addEventListener('click', function () { addRow(); });
  if (addBtn) addBtn.addEventListener('click', addAll);
  if (proofBtn && proofAvailable()) {
    proofBtn.hidden = false;
    proofBtn.addEventListener('click', downloadProof);
  }

  // Guard the single-item add: if the lines contain unfilled {variables}, a
  // normal add would engrave the literal braces. Block it (capture phase, ahead
  // of product-form.js) and point the customer to the bulk options. Single
  // orders of a template are still possible via one manual-entry row.
  if (singleAddBtn) {
    form.addEventListener('submit', function (e) {
      if (!isTemplateMode()) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      setStatus('Your lines contain {variables}. Use the bulk options below to fill them in — even for a single item (one row).', true);
      container.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, true);
  }

  refreshTemplateState();
})();
